/**
 * Language Detection Service
 * 
 * Maps country codes to supported language codes for automatic language detection
 */

import { GeolocationService } from './geolocationService';

export class LanguageDetectionService {
  // Mapping from country codes to language codes
  private static readonly COUNTRY_TO_LANGUAGE_MAP: Record<string, string> = {
    // Vietnamese
    'VN': 'vi',
    
    // Chinese
    'CN': 'zh',
    'TW': 'zh',
    'HK': 'zh',
    'MO': 'zh',
    'SG': 'zh', // Singapore has multiple languages, but Chinese is common
    
    // Thai
    'TH': 'th',
    
    // Korean
    'KR': 'ko',
    
    // Japanese
    'JP': 'ja',
    
    // English (explicit mapping for major English-speaking countries)
    'US': 'en',
    'GB': 'en',
    'AU': 'en',
    'CA': 'en',
    'NZ': 'en',
    'IE': 'en',
    'ZA': 'en',
    'IN': 'en'
  };

  // Supported languages in the platform
  private static readonly SUPPORTED_LANGUAGES = ['en', 'vi', 'zh', 'th', 'ko', 'ja'];

  /**
   * Detect language based on user's geolocation
   * Returns null if detection fails or if no suitable language mapping exists
   */
  static async detectLanguageFromLocation(): Promise<string | null> {
    try {
      console.log('🌍 Starting geolocation-based language detection...');
      
      const { countryCode } = await GeolocationService.detectCountry();
      console.log(`🌍 Detected country code: ${countryCode}`);
      
      const mappedLanguage = this.COUNTRY_TO_LANGUAGE_MAP[countryCode];
      
      if (mappedLanguage && this.SUPPORTED_LANGUAGES.includes(mappedLanguage)) {
        console.log(`✅ Mapped country ${countryCode} to language: ${mappedLanguage}`);
        return mappedLanguage;
      }
      
      console.log(`ℹ️ No language mapping for country ${countryCode}, will use fallback`);
      return null;
    } catch (error) {
      console.warn('❌ Failed to detect language from geolocation:', error);
      return null;
    }
  }

  /**
   * Get language from browser navigator with fallback
   */
  static getBrowserLanguage(): string {
    const browserLang = navigator.language.split('-')[0];
    return this.SUPPORTED_LANGUAGES.includes(browserLang) ? browserLang : 'en';
  }

  /**
   * Full language detection chain with priority order:
   * 1. localStorage (only if user explicitly chose)
   * 2. Geolocation-based detection
   * 3. Browser language
   * 4. Default to English
   */
  static async detectLanguage(): Promise<string> {
    console.log('🚀 Starting comprehensive language detection...');
    
    // Priority 1: localStorage (only if user explicitly chose)
    const storedLang = localStorage.getItem("language");
    const isManuallySet = localStorage.getItem("language-manually-set") === "true";
    if (storedLang && this.SUPPORTED_LANGUAGES.includes(storedLang) && isManuallySet) {
      console.log(`💾 Using manually set language preference: ${storedLang}`);
      return storedLang;
    }
    
    // Priority 2: Geolocation-based detection
    const geoLanguage = await this.detectLanguageFromLocation();
    if (geoLanguage) {
      console.log(`🌍 Using geolocation-based language: ${geoLanguage}`);
      return geoLanguage;
    }
    
    // Priority 3: Browser language
    const browserLanguage = this.getBrowserLanguage();
    console.log(`🌐 Using browser-based language: ${browserLanguage}`);
    return browserLanguage;
  }
}